import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Lobby } from "./pages/Lobby";
import { RoomManagement } from "./pages/RoomManagement";
import { WaitingRoom } from "./pages/WaitingRoom";
import { Game } from "./pages/Game";
import { initializeTheme, applyTheme } from "./themes/ThemeManager";
import { themeService } from "./services/theme.service";
import { authService } from "./services/auth.service";
import { socketClient } from "./socket/socket.client";
import { ThemeId } from "./themes/tokens";

function App() {
  const [themeInitialized, setThemeInitialized] = useState(false);

  // Initialize theme before rendering UI to prevent flash
  useEffect(() => {
    const initTheme = async () => {
      try {
        // Check if user is authenticated
        if (authService.isAuthenticated()) {
          // Fetch user theme from backend
          const userTheme = await themeService.getUserTheme();
          initializeTheme(userTheme);
        } else {
          // Guest users get default theme
          initializeTheme();
        }
      } catch (error) {
        console.error("[App] Error initializing theme:", error);
        // Fallback to default theme on error
        initializeTheme();
      } finally {
        setThemeInitialized(true);
      }
    };

    initTheme();
  }, []);

  // Listen for theme updates from other tabs/devices
  useEffect(() => {
    if (!themeInitialized) return;

    const handleThemeUpdate = (data: { userId: string; theme: string }) => {
      // Apply theme update from other tab/device
      const themeId = data.theme as ThemeId;
      if (themeId) {
        applyTheme(themeId);
      }
    };

    socketClient.onThemeUpdated(handleThemeUpdate);

    return () => {
      socketClient.off("THEME_UPDATED", handleThemeUpdate);
    };
  }, [themeInitialized]);

  // Don't render until theme is initialized to prevent flash
  if (!themeInitialized) {
    return null;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Lobby />} />
        <Route path="/rooms" element={<RoomManagement />} />
        <Route path="/room/:roomId" element={<WaitingRoom />} />
        <Route path="/game/:roomId" element={<Game />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
